import { Button, EmptyState, LoadingState } from '@buakei/ui';
import { AlertTriangle, Check, Circle } from 'lucide-react';
import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router';
import { describeAuthError } from '@/auth/identity-client';
import { checkPassword, passwordMeetsPolicy } from '@/auth/password-policy';
import { useAuth } from '@/auth/session';
import { parsePeriod, parsePlan, SelectedPlan } from './plan-quote';
import { destinationFor, inputClass } from './SignInPages';

/**
 * Sign-up (AUTH-001, ADR-010). Reached from "Create an account" or from a plan on the pricing
 * page, which arrives as `?plan=&period=` (untrusted; validated against @buakei/pricing).
 * Creating an account takes no payment and starts no subscription.
 */
export function SignUpPage() {
  const { session, status, accounts, notice } = useAuth();
  const [params] = useSearchParams();
  const planId = parsePlan(params.get('plan'));
  const period = parsePeriod(params.get('period'));

  useEffect(() => {
    document.title = 'Create your account · Buakei AI';
  }, []);

  if (session) return <Navigate to={destinationFor(session, null)} replace />;

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-6 px-4 py-12">
      <div className="flex flex-col gap-2">
        <h1 className="font-display text-4xl leading-tight text-navy-900">Create your account</h1>
        <p className="leading-relaxed text-muted-600">
          Already have an account?{' '}
          <Link to="/sign-in" className="font-medium text-navy-900 underline">
            Sign in
          </Link>
        </p>
      </div>
      {notice ? (
        <p role="alert" className="flex gap-2 text-sm text-danger-700">
          <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden /> {notice}
        </p>
      ) : null}
      {planId && period ? <SelectedPlan planId={planId} period={period} /> : null}
      {accounts ? (
        status === 'checking' ? (
          <LoadingState label="Loading your account" />
        ) : (
          <SignUpForm />
        )
      ) : status === 'checking' ? (
        <LoadingState label="Checking sign-up options" />
      ) : (
        <EmptyState title="Account sign-up is not available in this environment">
          <p>
            You can still{' '}
            <Link to="/sign-in" className="font-medium text-navy-900 underline">
              explore the demo
            </Link>{' '}
            with fictitious personas.
          </p>
        </EmptyState>
      )}
    </div>
  );
}

function SignUpForm() {
  const { accounts } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const ids = {
    email: useId(),
    password: useId(),
    rules: useId(),
    confirm: useId(),
    confirmError: useId(),
  };
  const passwordRef = useRef<HTMLInputElement>(null);
  const confirmRef = useRef<HTMLInputElement>(null);

  const rules = checkPassword(password);
  const policyMet = passwordMeetsPolicy(password);
  const mismatch = confirm.length > 0 && confirm !== password;
  const showConfirmError = mismatch || (submitted && confirm.length === 0);

  if (!accounts) return null;

  const run = async (action: () => Promise<string | null>) => {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      setMessage(await action());
    } catch (e) {
      setError(describeAuthError(e));
    } finally {
      setBusy(false);
    }
  };

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    setSubmitted(true);
    if (!policyMet) {
      setError('Choose a password that meets every rule below.');
      passwordRef.current?.focus();
      return;
    }
    if (confirm !== password) {
      setError('The passwords do not match.');
      confirmRef.current?.focus();
      return;
    }
    const address = email.trim();
    void run(async () => {
      await accounts.createAccount(address, password);
      return `Account created. We sent a verification email to ${address}.`;
    });
  };

  return (
    <section
      aria-labelledby="sign-up-form"
      className="flex flex-col gap-4 rounded-lg border border-line-200 bg-white p-5"
    >
      <h2 id="sign-up-form" className="text-lg font-semibold text-navy-900">
        Your details
      </h2>
      <Button
        variant="secondary"
        disabled={busy}
        onClick={() =>
          void run(async () => {
            await accounts.signInWithGoogle();
            return null;
          })
        }
      >
        Continue with Google
      </Button>
      <div className="flex items-center gap-3 text-sm text-muted-600" aria-hidden>
        <span className="h-px flex-1 bg-line-200" /> or <span className="h-px flex-1 bg-line-200" />
      </div>
      <form className="flex flex-col gap-4" onSubmit={onSubmit} noValidate>
        <div className="flex flex-col gap-1">
          <label htmlFor={ids.email} className="text-sm font-medium text-ink-900">
            Email
          </label>
          <input
            id={ids.email}
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={inputClass}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor={ids.password} className="text-sm font-medium text-ink-900">
            Password
          </label>
          <input
            id={ids.password}
            ref={passwordRef}
            type="password"
            required
            autoComplete="new-password"
            aria-describedby={ids.rules}
            aria-invalid={submitted && !policyMet}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className={inputClass}
          />
          <ul
            id={ids.rules}
            aria-label="Password rules"
            className="mt-1 flex flex-col gap-1 text-sm"
          >
            {rules.map((rule) => (
              <li
                key={rule.id}
                className={
                  rule.met
                    ? 'flex items-center gap-2 text-ok-700'
                    : 'flex items-center gap-2 text-muted-600'
                }
              >
                {rule.met ? (
                  <Check className="size-4 shrink-0" aria-hidden />
                ) : (
                  <Circle className="size-4 shrink-0" aria-hidden />
                )}
                <span>
                  <span className="sr-only">{rule.met ? 'Met: ' : 'Not met: '}</span>
                  {rule.label}
                </span>
              </li>
            ))}
          </ul>
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor={ids.confirm} className="text-sm font-medium text-ink-900">
            Confirm password
          </label>
          <input
            id={ids.confirm}
            ref={confirmRef}
            type="password"
            required
            autoComplete="new-password"
            aria-invalid={showConfirmError}
            aria-describedby={showConfirmError ? ids.confirmError : undefined}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className={inputClass}
          />
          {showConfirmError ? (
            <p id={ids.confirmError} className="text-sm text-danger-700">
              {mismatch ? 'The passwords do not match.' : 'Enter your password again.'}
            </p>
          ) : null}
        </div>
        <Button type="submit" disabled={busy}>
          {busy ? 'Please wait…' : 'Create account'}
        </Button>
      </form>
      {error ? (
        <p role="alert" className="flex gap-2 text-sm text-danger-700">
          <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden /> {error}
        </p>
      ) : null}
      <p role="status" aria-live="polite" className="text-sm text-ink-900">
        {message}
      </p>
    </section>
  );
}
